/**
 * Live data on the Gateways page and the gateway sheet (2026-10-30): each gateway's reading rate and its last 20
 * minutes from one read for the org (GET /v1/things/rates, useThingRatesQuery), the newest sign of life of a
 * gateway, the "Updated ... ago" text of a refresh, a clock that moves the ages on, and which instruments open their
 * values by themselves.
 *
 * The rate counts readings (one value of one point), not MQTT messages: a gateway that sends a poll in one message
 * sends many readings. A point with a deadband sends only on a change (and its heartbeat), so a healthy quiet gateway
 * can read 0.
 */

import { useEffect, useState } from "react";
import type { ThingRate, ThingRates } from "@/lib/api/types";
import type { TimeseriesPoint } from "@/lib/widgets/types";

/**
 * The rates by Thing id, or null when the rates are not known (not read yet, or the read failed: an older API has no
 * rates). A known map without a Thing means no reading in the window: its rate is 0.
 */
export function rateByThing(data: ThingRates | undefined): Map<string, ThingRate> | null {
  if (!data || !Array.isArray(data.items)) return null;
  return new Map(data.items.map((r) => [r.thing_id, r]));
}

/**
 * A Thing's rate as a sparkline: readings a second for each minute of the window, oldest first, each point at the end
 * of its minute (the last at `as_of`). Zeros when the Thing had no reading; empty when the rates are not known.
 */
export function rateSparkline(rates: ThingRates | undefined, thingId: string): TimeseriesPoint[] {
  if (!rates || !Array.isArray(rates.items)) return [];
  const end = Date.parse(rates.as_of);
  const bucketSec = rates.bucket_seconds > 0 ? rates.bucket_seconds : 60;
  const buckets = Math.max(1, Math.round((rates.window_seconds > 0 ? rates.window_seconds : 1200) / bucketSec));
  if (!Number.isFinite(end)) return [];
  const perMinute = rates.items.find((r) => r.thing_id === thingId)?.per_minute ?? [];
  // Right-aligned: the last count is the newest minute, whatever the length the server sent.
  const pad = buckets - perMinute.length;
  return Array.from({ length: buckets }, (_, i) => {
    const n = i - pad >= 0 ? perMinute[i - pad] : 0;
    return {
      t: new Date(end - (buckets - 1 - i) * bucketSec * 1000).toISOString(),
      v: Number.isFinite(n) ? n / bucketSec : 0,
    };
  });
}

/** Readings a second as the page shows them: "0", "<0.1", "1.6", "12". */
export function formatRate(perSec: number): string {
  if (!Number.isFinite(perSec) || perSec <= 0) return "0";
  if (perSec < 0.1) return "<0.1";
  if (perSec < 10) return perSec.toFixed(1);
  return String(Math.round(perSec));
}

/** The newest of several times (ISO), or undefined when none is a time. */
export function newestTime(...times: Array<string | null | undefined>): string | undefined {
  let best: string | undefined;
  let bestMs = -Infinity;
  for (const t of times) {
    const ms = t ? Date.parse(t) : NaN;
    if (Number.isFinite(ms) && ms > bestMs) {
      best = t!;
      bestMs = ms;
    }
  }
  return best;
}

/** How long ago a read finished, for "Updated ...": "just now", "12 s ago", "3 min ago", "2 h ago". Empty with no read. */
export function updatedAgo(at: number, now: number): string {
  if (!Number.isFinite(at) || at <= 0) return "";
  const s = Math.max(0, Math.floor((now - at) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s} s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  return `${Math.floor(m / 60)} h ago`;
}

/**
 * The last read of the values failed, and no read has worked since (ValuesReadFailed shows then). React Query
 * clears `isError` while it tries again when it has no data yet, so the times say it: the note stays up across the
 * tries instead of blinking.
 */
export function valuesReadFailing(q: { isError: boolean; errorUpdatedAt: number; dataUpdatedAt: number }): boolean {
  return q.isError || q.errorUpdatedAt > q.dataUpdatedAt;
}

/** Open every instrument's values when there are at most this many instruments... */
export const OPEN_ALL_MAX_INSTRUMENTS = 3;
/** ...with at most this many points among them; otherwise only the first opens (each open one polls every 5 s). */
export const OPEN_ALL_MAX_POINTS = 100;

/**
 * The instruments whose values open by themselves: all of them on a small gateway (a furnace panel's 8 points),
 * only the first on a big one (a pouring line's hundreds), so the sheet shows data at once without polling every
 * point of a big gateway.
 */
export function defaultOpenInstruments(items: Array<{ id: string; tag_count?: number | null }>): Set<string> {
  if (items.length === 0) return new Set();
  const points = items.reduce((n, it) => n + (it.tag_count ?? 0), 0);
  if (items.length <= OPEN_ALL_MAX_INSTRUMENTS && points <= OPEN_ALL_MAX_POINTS) {
    return new Set(items.map((it) => it.id));
  }
  return new Set([items[0].id]);
}

/** This moment, moved on every `everyMs` while mounted, so "... ago" texts move without a new read. */
export function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(timer);
  }, [everyMs]);
  return now;
}
